<template>
  <!-- Mode popup compact (sans el-form-item) -->
  <div v-if="isPopupMode" class="month-picker-popup" @click.stop>
    <el-date-picker
      :value="monthValue"
      @input="handleMonthChange"
      type="month"
      :placeholder="$t('planning.selectedMonth')"
      format="MMMM yyyy"
      value-format="yyyy-MM"
      size="mini"
      style="width: 100%;"
      :clearable="false"
      :append-to-body="false"
      :popper-class="'inline-month-picker'"
    />

    <!-- Boutons d'action -->
    <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 12px; padding-top: 8px; border-top: 1px solid #f0f0f0;">
      <el-link @click="clearMonth()" type="info" :underline="false" style="font-size: 12px;">
        <i class="el-icon-delete"></i> Réinitialiser
      </el-link>
      <el-button size="small" @click="handleValidate" type="primary" style="font-size: 13px;">
        <i class="el-icon-check"></i> Valider
      </el-button>
    </div>
  </div>

  <!-- Mode formulaire normal (avec el-form-item) -->
  <el-form-item 
    v-else
    :label="$t(field.label)" 
    :prop="field.name"
    :class="fieldClass"
  >
    <el-date-picker
      :value="monthValue"
      @input="handleMonthChange"
      type="month"
      :placeholder="$t(getProperty('placeholder', 'planning.selectedMonth'))"
      :format="getProperty('format', 'MMMM yyyy')"
      :value-format="getProperty('valueFormat', 'yyyy-MM')"
      :size="getProperty('size', 'medium')"
      :disabled="field.isDisabled"
      :clearable="getProperty('clearable', false)"
      :append-to-body="getProperty('appendToBody', false)"
      :popper-append-to-body="getProperty('popperAppendToBody', false)"
      style="width: 100%"
    />
    
    <!-- Aide contextuelle -->
    <div v-if="field.helpText" class="field-help-text">
      <small class="text-muted">{{ $t(field.helpText) }}</small>
    </div>
  </el-form-item>
</template>

<script>
export default {
  name: 'GenericFieldMonthPicker',
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Date, Object],
      default: () => null
    },
    isPopupMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    fieldClass() {
      return {
        'search-field': true,
        'search-field-month': true,
        'search-field-required': this.field.isRequired,
        'search-field-disabled': this.field.isDisabled
      }
    },
    
    monthValue() {
      // Conversion de la valeur selon le format attendu
      if (!this.value) {
        return null
      }

      // Si c'est un objet avec startDate/endDate, extraire le mois depuis startDate
      if (typeof this.value === 'object' && !Array.isArray(this.value) && this.value.startDate) {
        const dateObj = new Date(this.value.startDate)
        return this.formatMonthToString(dateObj)
      }

      // Si c'est une string ou Date directement
      if (typeof this.value === 'string' || this.value instanceof Date) {
        return this.value
      }

      return null
    }
  },
  methods: {
    handleMonthChange(monthValue) {
      if (!monthValue) {
        this.emitChange(null, null, null)
        return
      }

      // Calculer le premier et dernier jour du mois sélectionné
      const { startDate, endDate } = this.getFirstAndLastDayFromMonth(monthValue)
      
      console.log('🗓️ [GenericFieldMonthPicker.handleMonthChange]', { 
        monthValue, 
        startDate, 
        endDate,
        isPopupMode: this.isPopupMode
      })
      
      this.emitChange(monthValue, startDate, endDate)
    },

    getFirstAndLastDayFromMonth(monthValue) {
      // monthValue est au format "YYYY-MM" (ex: "2026-01")
      const [year, month] = monthValue.split('-').map(Number)
      
      // Premier jour du mois
      const firstDay = new Date(year, month - 1, 1)
      
      // Dernier jour du mois (1er jour du mois suivant - 1 jour)
      const lastDay = new Date(year, month, 0)
      
      return {
        startDate: this.formatDateToString(firstDay),
        endDate: this.formatDateToString(lastDay)
      }
    },

    emitChange(monthValue, startDate, endDate) {
      console.log('🔵 [GenericFieldMonthPicker.emitChange]', { 
        field: this.field.name, 
        monthValue,
        startDate, 
        endDate, 
        isPopupMode: this.isPopupMode 
      })
      
      // Format selon les besoins du champ
      let emittedValue
      
      if (this.isPopupMode) {
        // En mode popup, émettre un objet avec startDate/endDate
        emittedValue = startDate && endDate ? { startDate, endDate, monthValue } : null
      } else {
        // En mode formulaire normal, émettre le monthValue directement
        emittedValue = monthValue
      }
      
      this.$emit('input', emittedValue)
      this.$emit('change', { 
        field: this.field.name, 
        value: emittedValue,
        monthValue,
        startDate,
        endDate
      })
      
      // IMPORTANT: Émettre month-change pour synchroniser le calendrier
      // Calendar.vue a besoin de cet événement pour mettre à jour this.focus
      this.$emit('month-change', {
        field: this.field.name,
        monthValue,
        startDate,
        endDate
      })
      
      // IMPORTANT: Émettre range-update pour ApiSearchForm
      // ApiSearchForm a besoin de cet événement pour gérer les filtres startDate/endDate
      this.$emit('range-update', {
        field: this.field.name,
        minValue: startDate,
        maxValue: endDate,
        minField: 'startDate',
        maxField: 'endDate'
      })
      
      console.log('✅ [GenericFieldMonthPicker.emitChange] Émissions:', { 
        input: emittedValue,
        'month-change': { monthValue, startDate, endDate },
        'range-update': { field: this.field.name, minValue: startDate, maxValue: endDate }
      })
    },
    
    formatDateToString(date) {
      if (!date) return null
      if (typeof date === 'string') return date
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    formatMonthToString(date) {
      if (!date) return null
      if (typeof date === 'string') {
        // Si c'est déjà au format YYYY-MM-DD, extraire YYYY-MM
        if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return date.substring(0, 7)
        }
        return date
      }
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}`
    },

    clearMonth() {
      this.emitChange(null, null, null)
    },

    handleValidate() {
      // Émettre l'événement pour fermer la popup et déclencher la recherche
      this.$emit('close-and-search')
    },

    getProperty(propertyName, defaultValue = undefined) {
      if (!this.field.properties) {
        return defaultValue
      }
      
      // Support pour les propriétés en tant qu'objet ou tableau
      if (typeof this.field.properties === 'object' && !Array.isArray(this.field.properties)) {
        return this.field.properties[propertyName] || defaultValue
      }
      
      if (Array.isArray(this.field.properties)) {
        const property = this.field.properties.find(p => p.name === propertyName)
        if (!property) {
          return defaultValue
        }

        switch (property.type) {
          case 'boolean':
            return property.value === 'true' || property.value === true
          case 'number':
          case 'integer':
            return property.value ? Number(property.value) : defaultValue
          case 'string':
          default:
            return property.value || defaultValue
        }
      }

      return defaultValue
    }
  }
}
</script>

<style scoped>
.search-field {
  margin-bottom: 16px;
}

.search-field-required .el-form-item__label::after {
  content: ' *';
  color: #f56c6c;
}

.search-field-disabled {
  opacity: 0.6;
}

.field-help-text {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
}

.text-muted {
  color: #909399;
}

/* Styles du MonthPicker */
.search-field-month .el-date-editor {
  width: 100%;
}

.search-field-month .el-date-editor .el-input__inner {
  font-size: 14px;
  height: 36px;
  line-height: 36px;
}

.search-field-month .el-date-editor--small .el-input__inner {
  height: 32px;
  line-height: 32px;
}

/* Styles pour le mode popup */
.month-picker-popup {
  background: transparent;
  border: none;
  padding: 0;
  box-shadow: none;
  position: relative;
  width: 100%;
  max-width: 100%;
}

/* Forcer le MonthPicker à ne pas déborder */
.month-picker-popup .el-date-editor {
  width: 100% !important;
  max-width: 100% !important;
}

.month-picker-popup .el-date-editor .el-input__icon {
  display: none !important;
}
</style>

<style>
/* Style pour le monthPicker Element-UI inline */
.inline-month-picker {
  z-index: 999999 !important;
}

/* Masquer les boutons en double du calendrier Element UI */
.inline-month-picker .el-picker-panel__footer {
  display: none !important;
}

/* Masquer les boutons "Effacer" et "Confirmer" du calendrier natif */
.inline-month-picker .el-picker-panel__btn {
  display: none !important;
}

/* Optimiser l'espacement du calendrier mois */
.inline-month-picker .el-picker-panel {
  padding: 8px !important;
  width: 100% !important;
  min-width: 280px !important;
}

/* Optimiser l'affichage des mois */
.inline-month-picker .el-month-table {
  width: 100% !important;
}

.inline-month-picker .el-month-table td {
  padding: 8px 4px !important;
}

.inline-month-picker .el-month-table td .cell {
  font-size: 13px !important;
  padding: 6px 8px !important;
}
</style>
